import { Component, Input, OnInit, HostListener } from '@angular/core';
import { ConnectionResolver, MenuFilterService, UserPermissionsService } from '../../../core/services';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as authActions from '../../../auth/store/auth.actions';
import { select, Store } from '@ngrx/store';
import * as fromRoot from '../../../core/store';
import { Observable, Subscription } from 'rxjs';
import * as fromAuth from '../../../auth/store';
import { Router } from '@angular/router';
import { ResetPasswordComponent } from '../reset-password/reset-password.component';
import { MatDialog } from '@angular/material/dialog';
import { Location } from '@angular/common';
import { MatDrawer } from '@angular/material/sidenav';
import { IMenuItem, NHS_MENU_ROLES, CLINICIAN_MENU_ROLES } from '../../../core/services/navigation.service';
import { FeatureFlagModel } from '../../../views/settings/feature-flag/models/featureFlag.model';
import { DropdownOption } from '../ebo-dropdown/ebo-dropdown.component';
import { map, take } from 'rxjs/operators';

@Component({
  selector: 'ebo-top-header',
  templateUrl: './top-header.template.html',
  styleUrls: ['./top-header.component.scss']
})
export class TopHeaderComponent implements OnInit {
  @Input() drawer: MatDrawer;

  hubStatus = true;
  hideVASelector = false;
  isDashboardRoute = false;
  tablet = false;

  isBotCreated$: Observable<boolean>;
  hasEnvironmentPermission = true;
  fullName$: Observable<string>;
  picture$: Observable<string>;
  isAzure$: Observable<boolean>;
  virtualAgentName$: Observable<string>;
  bots$: Observable<{ id: number; name: string }[]>;
  botOptions$: Observable<DropdownOption[]>;
  selectedBotId$: Observable<number>;
  environment$: Observable<string>;
  logoSrc$: Observable<string>;

  private menuItems$: Subscription;
  private flags: FeatureFlagModel[];
  menuItems: IMenuItem[];

  @HostListener('window:resize')
  onResize(): void {
    this.checkScreenSize();
  }

  constructor(
    private location: Location,
    private router: Router,
    private permissionsService: UserPermissionsService,
    private snackBar: MatSnackBar,
    private store: Store<fromRoot.AppState>,
    private connectionResolver: ConnectionResolver,
    public dialog: MatDialog,
    private menuFilterService: MenuFilterService
  ) {
    // Hiding VA Selector if in conversation route
    this.router.events.subscribe(() => {
      this.hideVASelector = this.router.url.includes('/conversations');
    });
  }

  ngOnInit(): void {
    this.checkScreenSize();

    this.environment$ = this.store.pipe(select(fromAuth.getEnvironment));
    this.fullName$ = this.store.pipe(select(fromAuth.getFullName));
    this.picture$ = this.store.pipe(select(fromAuth.getPicture));
    this.isAzure$ = this.store.pipe(select(fromAuth.isAzure));
    this.isBotCreated$ = this.store.pipe(select(fromAuth.isBotCreated));
    this.virtualAgentName$ = this.store.pipe(select(fromAuth.getVirtualAgentName));
    this.bots$ = this.store.pipe(select(fromAuth.getBots));
    this.selectedBotId$ = this.store.pipe(select(fromAuth.getBotId));

    // Map bots to dropdown options
    this.botOptions$ = this.bots$.pipe(
      map(bots =>
        bots
          ? bots.map(bot => ({
              label: bot.name,
              value: bot.id
            }))
          : []
      )
    );

    // Select logo based on user role
    this.logoSrc$ = this.store.pipe(
      select(fromAuth.getUserRoles),
      map(roles => {
        if (Array.isArray(roles) && roles.some(r => NHS_MENU_ROLES.includes(r) || CLINICIAN_MENU_ROLES.includes(r))) {
          return 'assets/styles/new-theme/images/EBO-NHS-Logo.png';
        }
        return 'assets/styles/new-theme/images/EBO-Logo.png';
      })
    );

    this.isDashboardRoute = this.location.path().includes('/dashboard');

    this.connectionResolver.conversationHubStatus$.subscribe((status: boolean) => {
      this.hubStatus = status;
    });

    if (!this.permissionsService.hasPermission('environment.switch')) {
      this.hasEnvironmentPermission = false;
      this.environment$.pipe(take(1)).subscribe(environment => {
        if ((environment || '').toLowerCase() !== 'prod') {
          this.setEnvironment('Prod');
        }
      });
      // this.snackBar.open('Unauthorized operation - Production environment enable. User lacks environment permission.', 'Dismiss', { duration: 4000 });
    }

    this.menuItems$ = this.menuFilterService
      .getFilteredMenuItems({
        excludeNames: ['Dashboard'],
        featureFlags: this.flags
      })
      .subscribe((menuItems: IMenuItem[]) => {
        this.menuItems = menuItems;
      });

    this.router.events.subscribe(() => {
      this.isDashboardRoute = this.location.path().includes('/dashboard');
    });
  }

  setEnvironment(environment: string): void {
    this.store.dispatch(authActions.setEnvironment({ payload: { environment } }));
  }

  setBot(botId: number): void {
    this.store.dispatch(authActions.setBotId({ payload: { botId } }));
  }

  settings(): void {
    this.router.navigateByUrl('/settings');
  }

  signOut(): void {
    this.store.dispatch(authActions.logout());
  }

  resetOurPassword(): void {
    this.dialog.open(ResetPasswordComponent, {
      panelClass: 'ebo-small-dialog',
      disableClose: true
    });
  }

  private checkScreenSize(): void {
    this.tablet = window.innerWidth > 1080;
  }
}
